import { useEffect, useRef, useState } from 'react';

import type { UpsertCartResponse } from '@/hooks/mutations/booking/useUpsertCartMutation';
import { useDebounce } from '@/hooks/shared/useDebounce';
import { isValidEmail } from '@/utils/emailUtils';

import type {
  UseSyncGuestCustomerToCartParams,
  UseSyncGuestCustomerToCartResult,
} from './types';

const DEBOUNCE_MS = 500;

const buildKey = (
  info: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
  },
  cartRevision: string,
): string =>
  `${cartRevision}|${info.firstName}|${info.lastName}|${info.email}|${info.phone}`;

/**
 * While a guest fills the checkout contact form, validate their (debounced)
 * details so a membership discount can be shown BEFORE checkout.
 *
 * Validate — not a cart upsert — because the upsert path stamps
 * `item.customerId` once and never overwrites it, so a discount would stick even
 * after the guest changed their details. The stateless endpoint re-runs the
 * fuzzy-match every call. The real discount is applied at checkout (SQR-15069).
 */
export const useSyncGuestCustomerToCart = ({
  enabled,
  firstName,
  lastName,
  email,
  phone,
  cartRevision = '',
  onValidate,
}: UseSyncGuestCustomerToCartParams): UseSyncGuestCustomerToCartResult => {
  // What the guest has entered right now, before the debounce settles. Fields can
  // be undefined before their input registers, so coalesce. A half-typed email
  // counts as empty: the validate endpoint 422s on it.
  const trimmedEmail = (email ?? '').trim();
  const rawInfo = {
    firstName: (firstName ?? '').trim(),
    lastName: (lastName ?? '').trim(),
    email: isValidEmail(trimmedEmail) ? trimmedEmail : '',
    phone: (phone ?? '').trim(),
  };

  // ONE debounce window over the whole identity, not one per field. With a timer
  // each, a field that settled while the guest was still typing another would
  // fire a validation against a half-entered identity — matching nobody and
  // clearing a discount that reappears a keystroke later. Serialized so the
  // debounced value compares by value rather than by object identity.
  const debouncedInfoJson = useDebounce(JSON.stringify(rawInfo), DEBOUNCE_MS);

  const [validatedKey, setValidatedKey] = useState<string | null>(null);
  const [validatedCart, setValidatedCart] = useState<UpsertCartResponse | null>(
    null,
  );
  const [isValidating, setIsValidating] = useState(false);

  // Stops a slow earlier validation from overwriting a newer result.
  const latestKeyRef = useRef<string | null>(null);

  // The validated cart describes the cart as it was when validated. Once the
  // cart itself changes, that snapshot is stale and must not keep overriding the
  // breakdown — clear it immediately rather than showing old numbers as final.
  const validatedRevisionRef = useRef(cartRevision);
  useEffect(() => {
    if (validatedRevisionRef.current === cartRevision) return;
    validatedRevisionRef.current = cartRevision;
    latestKeyRef.current = null;
    setValidatedKey(null);
    setValidatedCart(null);
  }, [cartRevision]);

  useEffect(() => {
    if (!enabled || !onValidate) return;

    const info = JSON.parse(debouncedInfoJson) as typeof rawInfo;

    // The fuzzy-match needs a name plus one contact field. Below that there's
    // nothing to validate, so clear any discount already shown.
    const isMatchable = !!info.firstName && (!!info.email || !!info.phone);
    if (!isMatchable) {
      // Abandon any in-flight validation too, or its result would land on top
      // of this reset and resurrect a discount the details no longer earn.
      latestKeyRef.current = null;
      setValidatedKey(null);
      setValidatedCart(null);
      setIsValidating(false);
      return;
    }

    const key = buildKey(info, cartRevision);
    if (key === validatedKey) return;

    setValidatedKey(key);
    latestKeyRef.current = key;
    setIsValidating(true);

    // No preferredLanguage: it plays no part in the customer match, and guessing
    // one here would differ from the value checkout sends. Omitting is accepted;
    // `transformCartIntoPayloadCart` guarantees a blank one never goes out.
    onValidate(info)
      .then((result) => {
        if (latestKeyRef.current === key) setValidatedCart(result);
      })
      .catch(() => {
        if (latestKeyRef.current === key) setValidatedCart(null);
      })
      .finally(() => {
        if (latestKeyRef.current === key) setIsValidating(false);
      });
  }, [enabled, debouncedInfoJson, cartRevision, onValidate, validatedKey]);

  const isRawMatchable =
    !!rawInfo.firstName && (!!rawInfo.email || !!rawInfo.phone);

  // Without a handler nothing can ever validate, so this must not gate Book.
  const isPendingSync =
    enabled &&
    !!onValidate &&
    isRawMatchable &&
    (buildKey(rawInfo, cartRevision) !== validatedKey || isValidating);

  return {
    isPendingSync,
    isValidating: enabled && isValidating,
    validatedCart: enabled ? validatedCart : null,
  };
};
